import React, { useCallback, useEffect, useState } from "react";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ProductUI } from "@/shared/types";
import { catalogApi } from "@/shared/api/CatalogApi";
import { formatPhotoUrl } from "@/shared/utils/formatter";
import {
  formatUnitPriceLabel,
  getLineTotal,
} from "@/shared/utils/productPricing";
import "./styles/OrderAddItemsPicker.scss";

const NO_IMAGE = "/icons/no-image.svg";

interface OrderAddItemsPickerProps {
  open: boolean;
  onClose: () => void;
  cashId?: string;
  outletId?: string;
  onPick: (product: ProductUI, quantity: number) => void;
}

export const OrderAddItemsPicker: React.FC<OrderAddItemsPickerProps> = ({
  open,
  onClose,
  cashId,
  outletId,
  onPick,
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [products, setProducts] = useState<ProductUI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [qtyById, setQtyById] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, open]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setDebouncedSearch("");
      setProducts([]);
      setQtyById({});
      setPage(1);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);

    catalogApi
      .getProducts(1, 12, null, debouncedSearch, cashId, outletId)
      .then((data) => {
        if (cancelled) return;
        setProducts(data.products);
        setHasMore(data.totalPages > 1);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Не удалось загрузить товары"
        );
        setProducts([]);
        setHasMore(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, debouncedSearch, cashId, outletId]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const data = await catalogApi.getProducts(
        nextPage,
        12,
        null,
        debouncedSearch,
        cashId,
        outletId
      );
      setProducts((prev) => [...prev, ...data.products]);
      setPage(nextPage);
      setHasMore(nextPage < data.totalPages);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, debouncedSearch, cashId, outletId]);

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = NO_IMAGE;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="order-add-picker"
      PaperProps={{ className: "order-add-picker__dialog" }}
    >
      <div className="order-add-picker__content">
        <div className="order-add-picker__sticky">
          <div className="order-add-picker__header">
            <h3 className="order-add-picker__title">Добавить товар</h3>
            <IconButton
              className="order-add-picker__close"
              onClick={onClose}
              size="small"
              aria-label="Закрыть"
            >
              <CloseIcon />
            </IconButton>
          </div>

          <input
            className="order-add-picker__search"
            type="search"
            placeholder="Поиск по названию"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading && <p className="order-add-picker__hint">Загрузка...</p>}
        {error && <p className="order-add-picker__error">{error}</p>}
        {!loading && !error && products.length === 0 && (
          <p className="order-add-picker__hint">Ничего не найдено</p>
        )}

        <div className="order-add-picker__list">
          {products.map((product) => {
            const qty = qtyById[product.id] ?? 1;
            const imageUrl = formatPhotoUrl(product.photo);
            const lineTotal = getLineTotal(product, qty);
            return (
              <div key={product.id} className="order-add-picker__item">
                <div className="order-add-picker__media">
                  <img
                    src={imageUrl}
                    alt={product.name}
                    className="order-add-picker__image"
                    onError={handleImageError}
                  />
                </div>
                <div className="order-add-picker__body">
                  <span className="order-add-picker__name" title={product.name}>
                    {product.name}
                  </span>
                  <span className="order-add-picker__unit">
                    {formatUnitPriceLabel(product, qty)}
                  </span>
                  <div className="order-add-picker__controls">
                    <div className="order-add-picker__qty">
                      <button
                        type="button"
                        className="order-add-picker__qty-btn"
                        onClick={() =>
                          setQtyById((prev) => ({
                            ...prev,
                            [product.id]: Math.max(1, qty - 1),
                          }))
                        }
                        disabled={qty <= 1}
                        aria-label="Уменьшить количество"
                      >
                        −
                      </button>
                      <span className="order-add-picker__qty-value">{qty}</span>
                      <button
                        type="button"
                        className="order-add-picker__qty-btn"
                        onClick={() =>
                          setQtyById((prev) => ({
                            ...prev,
                            [product.id]: qty + 1,
                          }))
                        }
                        aria-label="Увеличить количество"
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className="order-add-picker__add-btn"
                      onClick={() => {
                        onPick(product, qty);
                        onClose();
                      }}
                    >
                      {lineTotal.toLocaleString("ru-RU")} ₽ · Добавить
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {hasMore && (
          <button
            type="button"
            className="order-add-picker__more"
            onClick={() => void loadMore()}
            disabled={loadingMore}
          >
            {loadingMore ? "Загрузка..." : "Показать ещё"}
          </button>
        )}
      </div>
    </Dialog>
  );
};
